<template>
  <modal
    v-if="showModal"
    @close="closeModal"
    class="fanatics-modal"
  >
    <template #modal-header="{ close }">
      <div class="title">Fanatics</div>
      <button
        class="modal-close tb-btn tb-btn-text sm low-em"
        @click="close"
      >
        <span class="icon icon-cross" />
      </button>
    </template>

    <template slot="modal-body">
      <div
        v-if="loading"
        class="text-center loading"
      >
        <span class="icon icon-spinner"></span>
      </div>

      <!-- Search -->
      <div class="filter-bar">
        <form
          class="tb-form-group"
          @submit.prevent="keywordSearch"
        >
          <i class="icon icon-magnifier"></i>
          <div class="fanatics-search">
            <input
              v-model="keyword"
              type="text"
              class="tb-input"
              placeholder="Search"
              :disabled="loading"
              ref="searchInput"
            >
            <button
              v-if="keyword"
              class="fanatics-search__clear"
              type="button"
              @click="clearSearch"
            >
              <span class="fa fa-close"></span>
            </button>
          </div>

          <button
            type="submit"
            :disabled="loading"
            class="tb-btn tb-btn-contained medium-em margin-left-md"
          >
            Search
          </button>
        </form>
        <!-- Chip filters -->
        <div class="flex">
          <div class="fanatics-chips">
            <p class="fanatics-chips__title">Gender</p>
  
            <div class="fanatics-chips__chips">
              <label
                v-for="(v, k) in genders"
                class="fanatics-chip"
                :key="k"
              >
                <span
                  :class="{
                    'fanatics-chip__content': true,
                    'fanatics-chip__content--checked': genderFilter.includes(k),
                  }"
                >
                  {{ k }}
                </span>
                <input
                  v-model="genderFilter"
                  :value="k"
                  type="checkbox"
                  class="fanatics-chip__input"
                >
              </label>
            </div>
          </div>
          <div class="fanatics-chips">
            <p class="fanatics-chips__title">Category</p>
  
            <div class="fanatics-chips__chips">
              <label
                v-for="(v, k) in keywordFilters"
                class="fanatics-chip"
                :key="k"
              >
                <span
                  :class="{
                    'fanatics-chip__content': true,
                    'fanatics-chip__content--checked': nameFilter.includes(k),
                  }"
                >
                  {{ k }}
                </span>
                <input
                  v-model="nameFilter"
                  :value="k"
                  type="checkbox"
                  class="fanatics-chip__input"
                >
              </label>
            </div>
          </div>
        </div>
      </div>

      <!-- Results -->
      <div class="margin-top-xl fanatics-results">
        <template v-if="filteredResults.length">
          <p class="text-os-low text-xs text-right">{{ filteredResults.length }} results found</p>
          <div
            v-for="r in filteredResults"
            :key="r.Id"
            class="fanatics-result"
            :class="{
              'fanatics-result': true,
              'fanatics-result--selected': product.id === r.Id
            }"
          >
            <div class="fanatics-result__click-area">
              <img
                :src="r.ImageUrl"
                class="fanatics-result__image"
                alt=""
              >
              <div class="fanatics-result__info">
                <p class="fanatics-result__details">{{ r.CurrentPrice }} {{ r.Currency }} <span>(ID: {{ r.CatalogItemId }})</span> </p>
                <button
                class="fanatics-result__button"
                  @click="chooseItem(r)"
                >
                  <span class="fanatics-result__name">{{ r.Name }}</span>
                </button>
              </div>
            </div>
            <a
              :href="r.Url"
              target="_blank"
              class="fanatics-result__link"
            >
              <span class="tgb-sr-only">Link to product page</span>
              <span class="fa fa-external-link"></span>
            </a>
          </div>
        </template>

        <div
          v-else-if="noResults"
          class="text-os-low text-xs text-center"
        >
          <p class="text-bold">
            No products found.
          </p>
          <p>
            Check your spelling or try filtering for better results.
          </p>
        </div>
        <p
          v-else
          class="text-os-low text-xs text-center"
        >Search a product to get started</p>
      </div>
    </template>
  </modal>
</template>

<script>
import Modal from '../modal/Modal.vue';

import FanaticsService from '../../services/fanatics';

export default {
  name: 'FanaticsModal',
  props: {
    value: {
      type: Boolean,
      required: true,
    },
    product: {
      type: Object,
      required: true,
    },
  },

  components: {
    Modal,
  },

  data() {
    return {
      keyword: '',
      results: [],
      loading: false,
      searched: false,
      genders: {
        Male: ['male'],
        Female: ['female'],
      },
      genderFilter: [],
      keywordFilters: {
        Jersey: ['jersey'],
        Hat: ['hat'],
        Sweatshirt: ['sweatshirt'],
        'T-Shirt': ['t-shirt'],
      },
      nameFilter: [],
    };
  },

  computed: {
    showModal: {
      get() {
        return this.value;
      },
      set(v) {
        this.$emit('input', v);
      },
    },
    filters() {
      return {
        Gender: this.genderFilter.reduce((arr, str) => this.genders[str], []),
        Name: this.nameFilter.reduce((arr, str) => this.keywordFilters[str], []),
      };
    },
    filteredResults() {
      if (Object.values(this.filters).some(arr => arr.length)) {
        return this.results.filter((r) => Object.keys(this.filters).every((property) => {
          const currentFilterTerms = this.filters[property];
          if (currentFilterTerms.length === 0) { // no filter
            return true;
          }

          return currentFilterTerms.some((term) => {
            const value = r[property].toLowerCase();
            if (property === 'Gender') {
              return value === term;
            }

            return value.includes(term);
          });
        }));
      }

      return this.results;
    },
    noResults() {
      return !this.loading && this.searched && !this.filteredResults.length;
    },
  },

  watch: {
    showModal(v) {
      if (v) {
        this.$nextTick(() => {
          // "autofocus" search input
          this.$refs.searchInput.focus();
        });
      }
    },
  },

  methods: {
    closeModal() {
      this.showModal = false;
    },
    clearSearch() {
      this.keyword = '';
      this.results = [];
    },
    async chooseItem(item) {
      this.$emit('select', item);
      this.closeModal();
    },
    async keywordSearch() {
      this.loading = true;
      try {
        if (this.keyword.length > 6 &&
          this.keyword.length < 16 &&
          /\d{7,15}/.test(this.keyword)
        ) {
          // product id search
          this.results = await FanaticsService.search({ product_id: this.keyword });
        } else {
          this.results = await FanaticsService.search({ keyword: this.keyword });
        }

      } catch (error) {
        console.error('Error doing fanatic keyword search', error);
      }

      this.loading = false;
      this.searched = true;
    },
  },
};
</script>

<style lang="scss">
@use '/src/sass/producer/defaults';

.fanatics-modal {
  // specificity overrides
  .modal-body {
    min-height: 350px;
  }
  .filter-bar {
    padding: 0;
  }
}

.fanatics-search {
  flex: 1;
  position: relative;

  .tb-input { //overrides
    padding-right: 30px;
  }

  &__clear {
    border: none;
    background-color: transparent;
    position: absolute;
    right: 0;
    top: 50%;
    transform: translate(-5px, -50%);
  }
}

.fanatics-chips {
  display: flex;
  align-items: center;

  & + & {
    margin-left: 24px;
  }
  
  &__title {
    color: defaults.$color-on-surface-medium;
    margin-bottom: 0;
    margin-right: 8px;
  }

  &__chips {
    display: flex;
    gap: 8px;
  }
}

.fanatics-chip {
  cursor: pointer;

  &__content {
    display: block;
    padding: 6px;
    border: 1px solid #4D6870;
    border-radius: 5px;
    color: defaults.$primary-blue;

    &--checked {
      border-color: #23CDF4;
      background-color: #253C42;
    }
  }

  &__input {
    position: absolute;
    opacity: 0;
  }
}

.fanatics-results {
  max-height: 70vh;
  overflow-y: auto;
}

.fanatics-result {
  display: flex;
  align-items: center;
  padding: 8px;
  border: 1px solid transparent;
  border-radius: 4px;

  & + & {
    margin-top: 8px;
  }

  &--selected {
    border-color:  defaults.$primary-blue;
  }

  &__image {
    height: 56px;
    width: 56px;
    object-fit: contain;
    margin: 8px 0;
  }

  &__info {
    padding: 0 12px;
    min-width: 0;
  }

  &__details {
    span {
      display: inline-block;
      margin-left: 8px;
      color: defaults.$color-on-surface-low;
    }
  }

  &__click-area {
    position: relative;
    flex: 1;
    min-width: 0;
    display: flex;
    align-items: center;
    margin-right: 8px;
  }

  &__button {
    background-color: transparent;
    border: none;
    padding: 0;
    text-align: left;
    max-width: 100%;

    &:hover > .fanatics-result__name {
      text-overflow: clip;
      white-space: normal;
    }

    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      height: 100%;
      width: 100%;
    }
  }

  &__name {
    @include defaults.ellipsis();
    display: block;
  }

  &__link {
    padding: 5px;
    display: block;
    margin-left: 8px;
  }
}
</style>
